/**
 * Levels 19–24 (Grades 10–12): the grand finale band. Castles on clouds, lost
 * temples, rainbow waterfalls, the Wild Coast, the Cape's fynbos under Table
 * Mountain and, at the very top, a golden sun temple above the clouds. Each
 * scene is a little more magical than the last.
 */
import type { ThemePack } from '../types.ts';
import { CLOUD_WHITE, FESTIVE, PARROTS } from './shared.ts';

const cloudCastle: ThemePack = {
  id: 'cloud-castle',
  level: 19,
  name: { en: 'Cloud Castle', af: 'Wolkekasteel' },
  tagline: { en: 'A shining castle floating on the clouds.', af: "'n Blink kasteel wat op die wolke dryf." },
  backgroundConcept:
    'High above the world: a white fairytale castle with turquoise spires and golden flags stands on a billowing cloud. Grassy sky islands drift nearby, hot-air balloons come visiting, a rainbow arcs behind the towers and the whole sky glows gold.',
  uiNotes: 'Pearl-white panels with royal-indigo text, a gold block border, indigo buttons and a rose accent. Polished marble bricks.',
  animationIdeas: ['The cloud sea rolls slowly past the castle', 'Balloons drift between the towers', 'Sky islands bob', 'Soft feathers float and twirl'],
  soundNotes: 'C lydian flute at 84 bpm over an airy pad, with a soft high-altitude wind.',
  palette: {
    skyTop: '#6cc4ff',
    skyBottom: '#fff1dc',
    wellBg: '#ffffff5c',
    wellBorder: '#ffffffcc',
    blockBg: '#fffdf8',
    blockBorder: '#f5b700',
    blockText: '#1c1b4d',
    brickA: '#f3eee6',
    brickB: '#e4dccf',
    brickMortar: '#ffffff',
    uiPanel: '#ffffffeb',
    uiText: '#1c1b4d',
    uiMuted: '#5d5c8a',
    accent: '#f06595',
    buttonBg: '#4c3fbf',
    buttonText: '#ffffff',
    good: '#23a45e',
    bad: '#e5606a',
  },
  brickStyle: 'marble',
  particles: { kind: 'feathers', density: 0.3, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#d9edff', midAt: 0.5, glow: { x: 0.5, y: 0.45, radius: 0.5, color: '#fff4d6' } },
    { kind: 'sun', x: 0.82, y: 0.12, radius: 0.07, color: '#ffe066', glow: '#fff5c7', rays: 14, rayColor: '#fff8dc' },
    { kind: 'rainbow', x: 0.4, y: 0.7, radius: 0.36, width: 0.06, opacity: 0.4 },
    { kind: 'clouds', count: 4, yMin: 0.08, yMax: 0.3, size: 0.2, color: CLOUD_WHITE, shade: '#e8e4ff', speed: 0.5, depth: 0.05 },
    {
      kind: 'islands',
      count: 3,
      yMin: 0.22,
      yMax: 0.42,
      size: 0.16,
      grass: '#8ad97a',
      rock: '#d1b49a',
      trees: '#5dbb5a',
      waterfall: '#a5ecff',
      spread: [0.02, 0.98],
      depth: 0.25,
    },
    { kind: 'balloons', count: 3, yMin: 0.14, yMax: 0.44, size: 0.06, colors: FESTIVE, speed: 1, depth: 0.3 },
    {
      kind: 'structure',
      type: 'castle',
      x: 0.55,
      baseline: 0.66,
      size: 0.44,
      color: '#fbf7f0',
      accent: '#3bc9db',
      trim: '#f5b700',
      mound: '#ffffff',
      moundStyle: 'cloud',
      depth: 0.4,
    },
    { kind: 'clouds', count: 6, yMin: 0.8, yMax: 0.94, size: 0.42, color: CLOUD_WHITE, shade: '#e6e2fa', speed: 0.3, depth: 0.45, seed: 5 },
    { kind: 'balloons', count: 2, yMin: 0.46, yMax: 0.62, size: 0.12, colors: FESTIVE, speed: 1.6, depth: 0.6, seed: 9 },
    { kind: 'clouds', count: 3, yMin: 0.9, yMax: 1, size: 0.5, color: CLOUD_WHITE, shade: '#ebe6ff', speed: 0.5, depth: 0.8, seed: 13 },
  ],
  audio: { key: 'C', scale: 'lydian', bpm: 84, lead: 'flute', pad: 'airy', ambience: 'wind' },
};

const jungleRuins: ThemePack = {
  id: 'jungle-ruins',
  level: 20,
  name: { en: 'Jungle Ruins', af: 'Oerwoudruïnes' },
  tagline: { en: 'Explore the lost temple in the rainforest.', af: 'Verken die verlore tempel in die reënwoud.' },
  backgroundConcept:
    'A lost city in a sunlit rainforest: a stepped stone temple with a golden shrine rises above the trees, broken columns and an archway wear capes of vines, a waterfall tumbles off a mossy cliff into a jade pool and bright blue butterflies flit through the sunbeams.',
  uiNotes: 'Pale moss panels with deep jungle-green text, jade buttons and a temple-gold accent. Carved stone bricks.',
  animationIdeas: ['Blue butterflies flit through the sunbeams', 'The waterfall streams into the pool', 'Vines sway', 'Parrots cross the clearing'],
  soundNotes: 'E dorian marimba at 94 bpm, warm pad, buzzing insects and distant bird calls.',
  palette: {
    skyTop: '#52c3ec',
    skyBottom: '#dff8d8',
    wellBg: '#ffffff5c',
    wellBorder: '#f2ffe8cc',
    blockBg: '#fbfff6',
    blockBorder: '#e0a800',
    blockText: '#142b1c',
    brickA: '#b8b39a',
    brickB: '#a39e86',
    brickMortar: '#eeeadb',
    uiPanel: '#f8fff3eb',
    uiText: '#142b1c',
    uiMuted: '#4f6552',
    accent: '#e8a200',
    buttonBg: '#11734b',
    buttonText: '#ffffff',
    good: '#23a15a',
    bad: '#e25f58',
  },
  brickStyle: 'stone',
  particles: { kind: 'butterflies', density: 0.4, color: '#4dabf7' },
  scene: [
    { kind: 'sky', mid: '#bff0dc', midAt: 0.55 },
    { kind: 'lightRays', x: 0.6, count: 7, spread: 1, color: '#fff7cc', opacity: 0.35 },
    { kind: 'clouds', count: 3, yMin: 0.06, yMax: 0.22, size: 0.2, color: CLOUD_WHITE, shade: '#d9f0e0', speed: 0.4, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.58, height: 0.14, color: '#8fd6a0', roughness: 0.6, mist: 0.5, depth: 0.05 },
    {
      kind: 'structure',
      type: 'temple',
      x: 0.62,
      baseline: 0.6,
      size: 0.3,
      color: '#c9c2a4',
      accent: '#f5b700',
      trim: '#6cc36a',
      mound: '#8fd09a',
      depth: 0.1,
    },
    { kind: 'trees', species: 'jungle', count: 5, baseline: 0.68, size: 0.26, color: '#5dbb6a', trunk: '#8a6a4a', depth: 0.15 },
    { kind: 'cliffs', side: 'left', width: 0.3, top: 0.34, color: '#a9a58f', stripes: '#97937d', moss: '#6cc36a', depth: 0.3 },
    { kind: 'waterfall', x: 0.16, top: 0.36, bottom: 0.8, width: 0.07, color: '#8fe3ff', foam: '#ffffff', rock: '#97937d', depth: 0.3 },
    { kind: 'water', top: 0.78, color: '#5fd4c8', deep: '#2fae9f', foam: '#ffffff', waves: 0.25, sparkle: true, depth: 0.35 },
    { kind: 'birds', style: 'parrot', count: 2, yMin: 0.2, yMax: 0.4, size: 0.05, colors: PARROTS, speed: 4, depth: 0.4 },
    { kind: 'ground', top: 0.87, color: '#63b85a', texture: 'moss', accent: '#4d9e48', depth: 0.6 },
    {
      kind: 'structure',
      type: 'ruins',
      x: 0.76,
      baseline: 0.9,
      size: 0.26,
      color: '#c4bd9f',
      accent: '#f5b700',
      trim: '#5cb85c',
      depth: 0.6,
    },
    { kind: 'trees', species: 'fern', count: 5, baseline: 0.96, size: 0.13, color: '#57bb4f', trunk: '#3f8a3a', sway: true, depth: 0.75 },
    { kind: 'vines', count: 8, length: 0.32, color: '#3f8f3a', leaf: '#62c054', flower: '#ff8fab', canopy: '#3fa04a', depth: 0.9 },
  ],
  audio: { key: 'E', scale: 'dorian', bpm: 94, lead: 'marimba', pad: 'warm', ambience: 'insects' },
};

const rainbowFalls: ThemePack = {
  id: 'rainbow-falls',
  level: 21,
  name: { en: 'Rainbow Falls', af: 'Reënboogvalle' },
  tagline: { en: 'Where waterfalls pour from floating islands.', af: 'Waar watervalle uit swewende eilande stort.' },
  backgroundConcept:
    'The most magical valley yet: a huge double rainbow, silver waterfalls pouring from sky islands and flower-topped lilac cliffs into a sparkling lake, rainbow crystals glinting at the water’s edge and blossom trees on the shore.',
  uiNotes: 'White panels with deep indigo text, a violet block border, violet buttons and a sunny orange accent. Crystal bricks in pastel rainbow tones.',
  animationIdeas: ['Waterfalls stream and foam', 'Islands bob above the falls', 'Crystals twinkle', 'Sparkles drift over the lake'],
  soundNotes: 'A-flat lydian bells at 88 bpm with a glass pad over a very soft rushing-water bed.',
  palette: {
    skyTop: '#62c3ff',
    skyBottom: '#fde7ff',
    wellBg: '#ffffff5c',
    wellBorder: '#ffffffcc',
    blockBg: '#fdfcff',
    blockBorder: '#9775fa',
    blockText: '#1f1947',
    brickA: '#d0bfff',
    brickB: '#a5d8ff',
    brickMortar: '#ffffff',
    uiPanel: '#ffffffeb',
    uiText: '#1f1947',
    uiMuted: '#5f5b8c',
    accent: '#f76707',
    buttonBg: '#6f42c1',
    buttonText: '#ffffff',
    good: '#22a468',
    bad: '#e55a78',
  },
  brickStyle: 'crystal',
  particles: { kind: 'sparkles', density: 0.5, color: '#fff4b3' },
  scene: [
    { kind: 'sky', mid: '#d6ecff', midAt: 0.5 },
    { kind: 'sun', x: 0.12, y: 0.1, radius: 0.06, color: '#fff07a', glow: '#fff8cc', rays: 12, rayColor: '#fffbe3' },
    { kind: 'rainbow', x: 0.5, y: 0.72, radius: 0.48, width: 0.09, opacity: 0.55, double: true },
    { kind: 'clouds', count: 4, yMin: 0.05, yMax: 0.25, size: 0.2, color: CLOUD_WHITE, shade: '#eadfff', speed: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'peaks', baseline: 0.62, height: 0.18, color: '#b9d6f0', snow: '#ffffff', snowLine: 0.6, mist: 0.5, depth: 0.05 },
    { kind: 'waterfall', x: 0.5, top: 0.47, bottom: 0.8, width: 0.1, color: '#b5eeff', foam: '#ffffff', rock: '#c8b6de', depth: 0.1 },
    {
      kind: 'islands',
      count: 3,
      yMin: 0.16,
      yMax: 0.34,
      size: 0.18,
      grass: '#86d97a',
      rock: '#d0b3a0',
      trees: '#ffa8cc',
      waterfall: '#bdf0ff',
      spread: [0.1, 0.9],
      depth: 0.2,
    },
    { kind: 'cliffs', side: 'left', width: 0.24, top: 0.3, color: '#d2b8e6', stripes: '#c3a6db', moss: '#8be08a', depth: 0.35 },
    { kind: 'waterfall', x: 0.12, top: 0.32, bottom: 0.82, width: 0.06, color: '#a8ecff', foam: '#ffffff', depth: 0.35 },
    { kind: 'cliffs', side: 'right', width: 0.24, top: 0.36, color: '#d2b8e6', stripes: '#c3a6db', moss: '#8be08a', depth: 0.35, seed: 3 },
    { kind: 'waterfall', x: 0.88, top: 0.38, bottom: 0.82, width: 0.06, color: '#a8ecff', foam: '#ffffff', depth: 0.35, seed: 4 },
    { kind: 'water', top: 0.8, color: '#7ae0f0', deep: '#48bde0', foam: '#ffffff', waves: 0.35, sparkle: true, depth: 0.4 },
    {
      kind: 'ground',
      top: 0.9,
      color: '#8fdc7a',
      texture: 'meadow',
      accent: '#6fc25e',
      flowers: ['#ffffff', '#ffb3d9', '#fff3a3', '#b197fc'],
      depth: 0.65,
    },
    {
      kind: 'crystals',
      count: 3,
      baseline: 0.92,
      size: 0.12,
      colors: ['#ffa8a8', '#ffd43b', '#8ce99a', '#74c0fc', '#b197fc'],
      spread: [0.3, 0.75],
      glint: true,
      depth: 0.65,
    },
    {
      kind: 'trees',
      species: 'blossom',
      count: 2,
      baseline: 0.93,
      size: 0.24,
      spread: [-0.04, 0.24],
      color: '#ffb3d9',
      trunk: '#8a5a44',
      accent: '#ffffff',
      depth: 0.7,
    },
  ],
  audio: { key: 'Ab', scale: 'lydian', bpm: 88, lead: 'bell', pad: 'glass', ambience: 'stream' },
};

const wildCoast: ThemePack = {
  id: 'wild-coast',
  level: 22,
  name: { en: 'Wild Coast', af: 'Wildekus' },
  tagline: { en: 'Green hills, rolling surf and a rock arch in the sea.', af: "Groen heuwels, rollende branders en 'n rotsboog in die see." },
  backgroundConcept:
    "South Africa's Eastern Cape coast: bright green clifftops dotted with turquoise-and-white rondavels, a great rock arch standing in the surf like the famous Hole in the Wall, long white breakers rolling onto a golden beach and gulls riding the sea wind.",
  uiNotes: 'Sea-foam white panels with deep ocean-blue text, a turquoise block border, ocean-blue buttons and a sunny aloe-orange accent. Shell bricks.',
  animationIdeas: ['Breakers roll in and sparkle', 'Sea spray drifts up from the rocks', 'Gulls hang on the wind', 'Clouds race by a little faster'],
  soundNotes: 'G mixolydian steel pan at 98 bpm, airy pad and rolling surf.',
  palette: {
    skyTop: '#45bdf5',
    skyBottom: '#d4f4ff',
    wellBg: '#ffffff59',
    wellBorder: '#ffffffcc',
    blockBg: '#fbfeff',
    blockBorder: '#20c997',
    blockText: '#0e2a40',
    brickA: '#f3dcc8',
    brickB: '#e8c9ae',
    brickMortar: '#fff8f0',
    uiPanel: '#ffffffe8',
    uiText: '#0e2a40',
    uiMuted: '#4f6a80',
    accent: '#ff8c1a',
    buttonBg: '#0b6fa4',
    buttonText: '#ffffff',
    good: '#1e9f5f',
    bad: '#e25a5f',
  },
  brickStyle: 'shell',
  particles: { kind: 'spray', density: 0.5, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#a8e4ff', midAt: 0.5 },
    { kind: 'sun', x: 0.72, y: 0.12, radius: 0.065, color: '#ffe45c', glow: '#fff3b0', rays: 12, rayColor: '#fff7cf' },
    { kind: 'clouds', count: 5, yMin: 0.05, yMax: 0.28, size: 0.22, color: CLOUD_WHITE, shade: '#d4ebfa', speed: 1.2, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.54, height: 0.08, color: '#8fd98a', roughness: 0.4, mist: 0.4, depth: 0.05 },
    { kind: 'water', top: 0.54, color: '#34bfe0', deep: '#1487c0', foam: '#ffffff', waves: 0.8, sparkle: true, depth: 0.1 },
    {
      kind: 'structure',
      type: 'arch',
      x: 0.28,
      baseline: 0.7,
      size: 0.22,
      color: '#c09a7c',
      accent: '#79d06a',
      trim: '#a07e62',
      depth: 0.2,
    },
    { kind: 'birds', style: 'gull', count: 4, yMin: 0.12, yMax: 0.32, size: 0.045, colors: ['#ffffff'], speed: 3.5, depth: 0.3 },
    { kind: 'cliffs', side: 'right', width: 0.45, top: 0.42, color: '#c7a17f', stripes: '#b08a69', moss: '#6fcf6a', depth: 0.35 },
    {
      kind: 'structure',
      type: 'huts',
      x: 0.8,
      baseline: 0.435,
      size: 0.09,
      color: '#8fd3e8',
      accent: '#d9a441',
      trim: '#ffffff',
      mound: '#6fcf6a',
      depth: 0.35,
    },
    { kind: 'ground', top: 0.86, color: '#ffe6b8', texture: 'sand', accent: '#f0cb8a', depth: 0.7 },
  ],
  audio: { key: 'G', scale: 'mixolydian', bpm: 98, lead: 'steelpan', pad: 'airy', ambience: 'waves' },
};

const fynbosPeaks: ThemePack = {
  id: 'fynbos-peaks',
  level: 23,
  name: { en: 'Fynbos Peaks', af: 'Fynbospieke' },
  tagline: { en: "Proteas bloom under the mountain's tablecloth.", af: 'Proteas blom onder die berg se tafeldoek.' },
  backgroundConcept:
    'The Cape floral kingdom: a great flat-topped mountain wearing its "tablecloth" of cloud rises above a sparkling bay. The slopes are covered in fynbos with giant pink king proteas, sunbirds dart between the flowers and a meadow of pincushions and daisies fills the foreground.',
  uiNotes: 'Warm white panels with deep slate text, a protea-pink block border, fynbos-green buttons and a pincushion-orange accent. Grey Table Mountain sandstone bricks.',
  animationIdeas: ['Sunbirds dart between the proteas', 'The tablecloth cloud rests on the plateau while other clouds drift by', 'Protea petals drift on the wind', 'Foreground proteas nod in the breeze'],
  soundNotes: 'A major kalimba at 96 bpm with an airy pad, birdsong and the chirp of sunbirds.',
  palette: {
    skyTop: '#4cbcf5',
    skyBottom: '#e4f6ff',
    wellBg: '#ffffff5c',
    wellBorder: '#ffffffcc',
    blockBg: '#fffcfd',
    blockBorder: '#f06595',
    blockText: '#1f2433',
    brickA: '#b9b3a8',
    brickB: '#a59f94',
    brickMortar: '#efebe4',
    uiPanel: '#fffcfbeb',
    uiText: '#1f2433',
    uiMuted: '#5b6072',
    accent: '#fd7e14',
    buttonBg: '#237a35',
    buttonText: '#ffffff',
    good: '#23a15a',
    bad: '#e05a64',
  },
  brickStyle: 'stone',
  particles: { kind: 'petals', density: 0.35, color: '#ff9fb5' },
  scene: [
    { kind: 'sky', mid: '#b3e3ff', midAt: 0.5 },
    { kind: 'sun', x: 0.15, y: 0.12, radius: 0.06, color: '#ffe45c', glow: '#fff3b0', rays: 10, rayColor: '#fff7cf' },
    { kind: 'clouds', count: 3, yMin: 0.06, yMax: 0.22, size: 0.2, color: CLOUD_WHITE, shade: '#d6ebf7', speed: 0.6, depth: 0.05 },
    { kind: 'ridges', shape: 'table', baseline: 0.62, height: 0.3, color: '#a9b8c8', stripes: '#9aabbd', roughness: 0.3, depth: 0.05 },
    { kind: 'clouds', count: 1, yMin: 0.315, yMax: 0.325, size: 0.55, color: CLOUD_WHITE, shade: '#e3edf6', speed: 0, style: 'long', depth: 0.05, seed: 2 },
    { kind: 'water', top: 0.62, color: '#4cc4e8', deep: '#2b9fd0', foam: '#ffffff', waves: 0.3, sparkle: true, depth: 0.1 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.8, height: 0.14, color: '#8cc56a', roughness: 0.6, depth: 0.3 },
    { kind: 'trees', species: 'protea', count: 6, baseline: 0.81, size: 0.12, color: '#6fa85a', trunk: '#6b5a3c', accent: '#ff85a8', depth: 0.3 },
    { kind: 'birds', style: 'sunbird', count: 3, yMin: 0.45, yMax: 0.7, size: 0.035, colors: ['#2fb380', '#ff6b3d', '#4dabf7'], speed: 3, depth: 0.5 },
    {
      kind: 'ground',
      top: 0.9,
      color: '#86c46a',
      texture: 'meadow',
      accent: '#6aa852',
      flowers: ['#ff85a8', '#ffb13b', '#ffe066', '#ffffff'],
      depth: 0.75,
    },
    {
      kind: 'trees',
      species: 'protea',
      count: 3,
      baseline: 0.97,
      size: 0.2,
      spread: [0, 1],
      color: '#5f9c4c',
      trunk: '#5a4a32',
      accent: '#ff6f91',
      sway: true,
      depth: 0.75,
    },
  ],
  audio: { key: 'A', scale: 'major', bpm: 96, lead: 'kalimba', pad: 'airy', ambience: 'birds' },
};

const sunTempleSummit: ThemePack = {
  id: 'sun-temple-summit',
  level: 24,
  name: { en: 'Sun Temple Summit', af: 'Sontempel-kruin' },
  tagline: { en: 'The golden temple at the top of the world!', af: 'Die goue tempel bo-op die wêreld!' },
  backgroundConcept:
    'The grand finale: a gleaming ivory-and-gold temple crowns a summit high above the clouds, bathed in a giant golden sun with sweeping rays. Snowy peaks and sky islands surround it, a rainbow arches overhead, balloons and bunting celebrate and golden confetti rains down.',
  uiNotes: 'Ivory panels with deep royal-purple text, a gold block border, royal-purple buttons and a sunburst-gold accent. Golden sandstone bricks.',
  animationIdeas: ['Golden sun rays turn majestically', 'Confetti celebrates your climb', 'The cloud sea rolls below the summit', 'Balloons rise on warm air'],
  soundNotes: 'D lydian flute at a proud 104 bpm with a glass pad and a soft summit wind — the brightest, most triumphant loop.',
  palette: {
    skyTop: '#62bff5',
    skyBottom: '#fff0cf',
    wellBg: '#fffdf75c',
    wellBorder: '#fff6dccc',
    blockBg: '#fffdf6',
    blockBorder: '#f5b700',
    blockText: '#24123f',
    brickA: '#f2c46b',
    brickB: '#e3b058',
    brickMortar: '#fff4d8',
    uiPanel: '#fffcf3eb',
    uiText: '#24123f',
    uiMuted: '#655577',
    accent: '#f59f00',
    buttonBg: '#5f3dc4',
    buttonText: '#ffffff',
    good: '#23a15a',
    bad: '#e05a64',
  },
  brickStyle: 'sandstone',
  particles: { kind: 'confetti', density: 0.4, color: '#ffd43b' },
  scene: [
    { kind: 'sky', mid: '#cfeaff', midAt: 0.45, glow: { x: 0.5, y: 0.2, radius: 0.7, color: '#fff3c4' } },
    { kind: 'sun', x: 0.5, y: 0.17, radius: 0.1, color: '#ffd43b', glow: '#fff0a8', rays: 20, rayColor: '#ffe594' },
    { kind: 'rainbow', x: 0.5, y: 0.64, radius: 0.46, width: 0.07, opacity: 0.4 },
    { kind: 'clouds', count: 3, yMin: 0.06, yMax: 0.2, size: 0.2, color: CLOUD_WHITE, shade: '#ffe8cc', speed: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'peaks', baseline: 0.62, height: 0.26, color: '#b8c9e6', snow: '#ffffff', snowLine: 0.5, mist: 1, depth: 0.05 },
    {
      kind: 'islands',
      count: 2,
      yMin: 0.28,
      yMax: 0.4,
      size: 0.15,
      grass: '#8ad97a',
      rock: '#d6b99e',
      trees: '#5dbb5a',
      waterfall: '#b5eeff',
      depth: 0.2,
    },
    {
      kind: 'structure',
      type: 'temple',
      x: 0.5,
      baseline: 0.57,
      size: 0.33,
      color: '#fff8ec',
      accent: '#f5b700',
      trim: '#ffd43b',
      mound: '#c3b3e3',
      moundStyle: 'peak',
      depth: 0.3,
    },
    { kind: 'balloons', count: 4, yMin: 0.24, yMax: 0.5, size: 0.07, colors: FESTIVE, speed: 1.2, depth: 0.35 },
    { kind: 'clouds', count: 6, yMin: 0.62, yMax: 0.8, size: 0.4, color: CLOUD_WHITE, shade: '#f3e3d0', speed: 0.3, depth: 0.45, seed: 7 },
    { kind: 'clouds', count: 4, yMin: 0.86, yMax: 1, size: 0.5, color: CLOUD_WHITE, shade: '#f5e6d6', speed: 0.45, depth: 0.8, seed: 15 },
    { kind: 'bunting', y: 0.02, sag: 0.05, rows: 2, colors: ['#ffd43b', '#f06595', '#9775fa', '#4dabf7', '#69db7c'], string: '#ffffff', depth: 0.9 },
  ],
  audio: { key: 'D', scale: 'lydian', bpm: 104, lead: 'flute', pad: 'glass', ambience: 'wind' },
};

export const LEVELS_19_24: ThemePack[] = [cloudCastle, jungleRuins, rainbowFalls, wildCoast, fynbosPeaks, sunTempleSummit];
